import React from "react";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import { TitleSubtitleProps } from "../../types/screensTypes";
import { useRemainingTokens } from "../../hooks/useRemainingTokens";
import styles from "./TitleSubtitle.module.css";

const layoutClass: Record<NonNullable<TitleSubtitleProps["layout"]>, string> = {
    default: "",
    step: styles.layoutStep,
    titleField: styles.layoutTitleField,
    location: styles.layoutLocation,
    photos: styles.layoutPhotos,
    extra: styles.layoutExtra,
    access: styles.layoutAccess,
    museum: styles.layoutMuseum,
    stories: styles.layoutStories,
    architecture: styles.layoutArchitecture,
    busTracking: styles.layoutBusTracking,
    music: styles.layoutMusic,
    create: styles.layoutCreate,
    incomplete: styles.layoutIncomplete,
    publish: styles.layoutPublish,
    edit: styles.layoutEdit,
    map: styles.layoutMap,
    mapCompact: styles.layoutMapCompact,
    scanTheme: styles.layoutScanTheme,
    objects: styles.layoutObjects,
    entityForm: styles.layoutEntityForm,
};

const TitleSubtitle: React.FC<TitleSubtitleProps & { titleSubtitleClassName?: string }> = ({
    title,
    subtitle,
    showText = false,
    showAudio = false,
    addMargin = true,
    classNameTitle,
    classNameSubtitle,
    titleSubtitleClassName,
    shouldIncludeMarginStart = false,
    tooltip,
    layout = "default",
}: TitleSubtitleProps & { titleSubtitleClassName?: string }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data: remainingTokens } = useRemainingTokens();

    const LOW_TOKEN_THRESHOLD = 200;

    const getThresholdClass = (value: number | undefined | null): string => {
        if (value === undefined || value === null) return "";
        if (value <= 0) return styles.remainingTokensDanger;
        if (value < LOW_TOKEN_THRESHOLD) return styles.remainingTokensWarning;
        return "";
    };

    const renderRemainingTokens = () => {
        if (!remainingTokens) return null;
        const textClass = getThresholdClass(remainingTokens.remainigTextTokens);
        const audioClass = getThresholdClass(remainingTokens.remainigAudioTokens);

        if (showText && !showAudio) {
            return (
                <p className={[styles.remainingTokens, textClass].filter(Boolean).join(" ")}>
                    {t("wordLeftForText")} {remainingTokens.remainigTextTokens}
                </p>
            );
        }
        if (showAudio && !showText) {
            return (
                <p className={[styles.remainingTokens, audioClass].filter(Boolean).join(" ")}>
                    {t("wordLeftForAudio")} {remainingTokens.remainigAudioTokens}
                </p>
            );
        }
        if (showText && showAudio) {
            return (
                <div className={styles.remainingTokens}>
                    <p className={textClass} style={{ margin: 0 }}>
                        {t("wordLeftForText")} {remainingTokens.remainigTextTokens}
                    </p>
                    <p className={audioClass} style={{ margin: 0 }}>
                        {t("wordLeftForAudio")} {remainingTokens.remainigAudioTokens}
                    </p>
                </div>
            );
        }
        return null;
    };

    const rootClass = [addMargin ? styles.root : styles.rootNoMargin, layoutClass[layout], titleSubtitleClassName]
        .filter(Boolean)
        .join(" ");

    const titleClass = classNameTitle ?? styles.title;
    const subtitleClass = classNameSubtitle ?? styles.subtitle;

    const hasSubtitle = Boolean(subtitle && String(subtitle).trim());

    const titleRow = (
        <div className={styles.titleRow}>
            <h1 className={titleClass}>{title}</h1>
            {tooltip && !hasSubtitle && (
                <Tooltip title={tooltip} arrow>
                    <span className={`tooltip-icon ${styles.tooltipIcon}`}>i</span>
                </Tooltip>
            )}
        </div>
    );

    const subtitleRow = hasSubtitle ? (
        <div className={styles.subtitleRow}>
            <p className={subtitleClass}>{subtitle}</p>
            {tooltip && (
                <Tooltip title={tooltip} arrow>
                    <span className={`tooltip-icon ${styles.tooltipIcon}`}>i</span>
                </Tooltip>
            )}
        </div>
    ) : null;

    const content = (
        <>
            <div>
                {titleRow}
                {subtitleRow}
            </div>
            <div className={styles.tokenInfo}>{renderRemainingTokens()}</div>
        </>
    );

    if (shouldIncludeMarginStart) {
        return (
            <div className={styles.pageTitle}>
                <div className={rootClass}>{content}</div>
            </div>
        );
    }

    return <div className={rootClass}>{content}</div>;
};

export default TitleSubtitle;
